import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  TemplateMessageData,
  TemplateResponseData,
} from "@smart/bridge-templates-basic";
import {
  Button,
  ProgressBar,
  SegmentedControl,
} from "@smart/itops-sb-design-system-dom";
import { useSmokeballApp } from "@smart/itops-smokeball-app-dom";

import { Actions } from "./actions";
import { FormInfo } from "./form-info";
import { LeaveWithoutPublishingModal } from "./leave-without-publishing-modal";
import {
  FormBuilderTabOptions,
  LanguageKey,
  SupportedLanguageOption,
} from "../../../types";
import { EditingFormInfo } from "../../list-forms/form";
import {
  GqlField,
  GqlFormCategory,
  GqlGroup,
  GqlMatterType,
  GqlSetting,
} from "../../types";
import { usePublish } from "../publish-form";
import { GqlSubmissionLinkHash } from "../types";

type FormHeadingProps = {
  formUri: string;
  formTitle: string;
  tab?: FormBuilderTabOptions;
  isMatterSpecific: boolean;
  isFormActive: boolean | undefined;
  canActivate?: boolean;
  canUpdate?: boolean;
  disabled?: boolean;
  loading: {
    form: boolean;
    matterTypes: boolean;
  };
  updating?: boolean;
  setTab: React.Dispatch<React.SetStateAction<FormBuilderTabOptions>>;
  onActiveToggleClick: () => Promise<void>;
  matterTypeNames: string[];
  matterTypeLocations: string[];
  onFormTitleUpdate: (title: string) => Promise<void>;
  validateFormTitle: (title: string) => boolean;
  saveMessageTemplates: (messages: {
    new?: string | null;
    reminder?: string | null;
    confirmation?: string;
    confirmationMode?: "message" | "redirect";
    redirectUrl?: string | null;
  }) => Promise<void>;
  currentClientMessages?: {
    new?: string | null;
    reminder?: string | null;
    confirmation?: string | null;
  };
  currentRedirectUrl?: string | null;
  defaultClientMessage?: {
    matter?: string | null;
    lead?: string | null;
    reminder?: string | null;
    confirmation?: string | null;
  } | null;
  messagePlaceholderData: Partial<TemplateMessageData>;
  responsePlaceholderData: Partial<TemplateResponseData>;
  updateFormInfo: (values: EditingFormInfo) => Promise<void>;
  setting?: GqlSetting | null;
  category?: GqlFormCategory;
  matterTypes: GqlMatterType[];
  groups?: GqlGroup[] | null;
  fields?: GqlField[] | null;
  submissionLinkHashes?: GqlSubmissionLinkHash[];
  clearIntakeForm: () => void;
  disableTabs?: boolean;
  creationStatus?: string | null;
  onSaveLanguages: (languages: LanguageKey[]) => Promise<void>;
  supportedLanguages?: SupportedLanguageOption[];
  viewingLanguage: LanguageKey;
  setViewingLanguage: (language: LanguageKey) => void;
  hasUnpublishedChanges?: boolean;
};

const FormHeading = ({
  formUri,
  formTitle,
  tab,
  isMatterSpecific,
  isFormActive,
  canActivate,
  canUpdate,
  disabled,
  loading,
  updating,
  setTab,
  onActiveToggleClick,
  matterTypeNames,
  matterTypeLocations,
  onFormTitleUpdate,
  validateFormTitle,
  saveMessageTemplates,
  currentClientMessages,
  currentRedirectUrl,
  defaultClientMessage,
  messagePlaceholderData,
  responsePlaceholderData,
  updateFormInfo,
  setting,
  category,
  matterTypes,
  groups,
  fields,
  submissionLinkHashes,
  clearIntakeForm,
  disableTabs,
  creationStatus,
  onSaveLanguages,
  supportedLanguages,
  viewingLanguage,
  setViewingLanguage,
  hasUnpublishedChanges,
}: FormHeadingProps) => {
  const navigate = useNavigate();
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const { setPublishModalOpen } = usePublish();
  const { host, hostType } = useSmokeballApp();
  const continueCloseRef = useRef<((shouldClose: boolean) => void) | null>(
    null,
  );
  const hasUnpublishedChangesRef = useRef(hasUnpublishedChanges);
  /**
   * The desktop host re-fires interceptClose once after it receives a continueClose(false)
   * This happens the first time per session. Rather than guessing which call
   * is "the first", arm a short-lived suppression window each time and let
   * it auto-expire if nothing consumes it. Once https://smokeball.atlassian.net/browse/LINK-3793
   * is resolved, we can remove this suppression mechanism.
   */
  const suppressNextInterceptRef = useRef(false);
  const suppressTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    hasUnpublishedChangesRef.current = hasUnpublishedChanges;
  }, [hasUnpublishedChanges]);

  useEffect(
    () => () => {
      if (suppressTimeoutRef.current) clearTimeout(suppressTimeoutRef.current);
    },
    [],
  );

  useEffect(() => {
    const isDesktopV10Plus =
      hostType === "windows" &&
      host?.isEarlierThanVersion({ major: 10, minor: 0 }) === false;
    if ((hostType !== "web" && !isDesktopV10Plus) || !host) return undefined;
    host.interceptClose((continueClose) => {
      if (suppressNextInterceptRef.current) {
        suppressNextInterceptRef.current = false;
        if (suppressTimeoutRef.current) {
          clearTimeout(suppressTimeoutRef.current);
          suppressTimeoutRef.current = null;
        }
        continueClose(false);
        return;
      }
      if (hasUnpublishedChangesRef.current) {
        continueCloseRef.current = continueClose;
        setLeaveModalOpen(true);
      } else {
        continueClose(true);
        continueCloseRef.current = null;
      }
    });

    return () => {
      host.interceptClose((continueClose) => continueClose(true));
    };
  }, [hostType, !!host, host?.version]);

  const leave = () => {
    navigate(`/embedded/${isMatterSpecific ? "tab" : "builder"}`);
  };
  const handleLeave = () => {
    if (continueCloseRef.current) {
      continueCloseRef.current(true);
      continueCloseRef.current = null;
      setLeaveModalOpen(false);
    } else {
      leave();
    }
  };
  const handleBackClick = () => {
    if (hasUnpublishedChanges && canUpdate) {
      setLeaveModalOpen(true);
    } else {
      leave();
    }
  };

  const tabs = [
    {
      label: "Edit",
      disabled: disableTabs,
      onClick: () => setTab("questions"),
    },
    {
      label: "Preview",
      disabled: disableTabs,
      onClick: () => setTab("preview"),
    },
    ...(category === "lead"
      ? [
          {
            label: "Share",
            disabled: disableTabs,
            onClick: () => setTab("share"),
          },
        ]
      : []),
  ];

  const tabIndex: Record<FormBuilderTabOptions, number> = {
    questions: 0,
    preview: 1,
    share: 2,
  };

  return (
    <>
      <div className="relative pl-[2.4rem] pr-[4rem] py-[0.8rem] border-neutral-100 border-solid border-b">
        {updating && (
          <div className="absolute w-full bottom-0 left-0">
            <ProgressBar />
          </div>
        )}
        <div className="flex items-center gap-large">
          <Button
            dataTestId="back-button"
            leftIcon="regularArrowLeft"
            variant="primarySubtle"
            onClick={handleBackClick}
          />
          <FormInfo
            formTitle={formTitle}
            matterTypeNames={matterTypeNames}
            matterTypeLocations={matterTypeLocations}
            disabled={disabled}
            isMatterSpecific={isMatterSpecific}
            loading={loading}
            onFormTitleUpdate={onFormTitleUpdate}
            validateFormTitle={validateFormTitle}
            updateFormInfo={updateFormInfo}
            setting={setting}
            category={category}
            matterTypes={matterTypes}
            groups={groups}
            fields={fields}
            submissionLinkHashes={submissionLinkHashes}
          />
          <div className="flex justify-center flex-1">
            <SegmentedControl
              index={tabIndex[tab || "questions"]}
              items={tabs}
            />
          </div>
          <Actions
            formUri={formUri}
            formTitle={formTitle}
            isMatterSpecific={isMatterSpecific}
            isFormActive={isFormActive}
            canActivate={canActivate}
            disabled={disabled}
            loading={{
              form: loading.form && !formTitle,
              matterTypes: loading.matterTypes,
            }}
            canUpdate={canUpdate}
            onActiveToggleClick={onActiveToggleClick}
            saveMessageTemplates={saveMessageTemplates}
            currentClientMessages={currentClientMessages}
            currentRedirectUrl={currentRedirectUrl}
            defaultClientMessage={defaultClientMessage}
            messagePlaceholderData={messagePlaceholderData}
            responsePlaceholderData={responsePlaceholderData}
            isPreview={tab === "preview"}
            clearIntakeForm={clearIntakeForm}
            formCategory={category}
            creationStatus={creationStatus}
            onSaveLanguages={onSaveLanguages}
            supportedLanguages={supportedLanguages}
            viewingLanguage={viewingLanguage}
            setViewingLanguage={setViewingLanguage}
            updateFormInfo={updateFormInfo}
            setting={setting}
            matterTypeLocations={matterTypeLocations}
            matterTypes={matterTypes}
            groups={groups}
            fields={fields}
            submissionLinkHashes={submissionLinkHashes}
          />
        </div>
      </div>
      <LeaveWithoutPublishingModal
        open={leaveModalOpen}
        onLeave={handleLeave}
        onPublish={() => {
          setLeaveModalOpen(false);
          if (continueCloseRef.current) {
            suppressNextInterceptRef.current = true;
            suppressTimeoutRef.current = setTimeout(() => {
              suppressNextInterceptRef.current = false;
              suppressTimeoutRef.current = null;
            }, 1000);
            continueCloseRef.current(false);
            continueCloseRef.current = null;
          }
          setPublishModalOpen(true);
        }}
      />
    </>
  );
};

export { FormHeading, FormHeadingProps };
